import React, { useCallback, useState, useEffect } from 'react';
import { useStore } from '../../store';
import { useNavigate, Link } from 'react-router-dom';
import { getLastSyncCheck } from '../../Utils/Functions/Dexie-Setup';

export const version = "1.2.5";

function Settings() {
  // --- Hooks and State ---
  const {
    isAuthed,
    isOnline,
    language,
    setLanguage,
    hours,
    setHours,
    signOut,
    fetchAndStoreAllTables,
    translation = {},
  } = useStore();

  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // --- Handlers ---
  const changeHours = useCallback((value) => {
    let num = Number(value);
    if (isNaN(num)) num = null;
    if (num < 0) num = 0;
    if (num > 4300) num = 4300;
    setHours(num !== null ? Math.floor(num) : null);
  }, [setHours]);

  const handleRefreshData = () => {
    setRefreshing(true);
    fetchAndStoreAllTables().finally(() => setRefreshing(false));
  };

  const handleLogout = () => {
    if (window.confirm(translation.confirmLogout)) {
      signOut();
    }
  };

  // --- Effects ---
  useEffect(() => {
    if (!isAuthed) {
      navigate("/auth");
    }
  }, [isAuthed, navigate]);

  useEffect(() => {
    getLastSyncCheck().then(setLastSynced);
  }, []);


  // --- Style Definitions ---
  const cardStyle = "bg-white rounded-xl shadow-md p-4";
  const labelStyle = "block text-sm font-medium text-gray-600 mb-2";
  const buttonBaseStyle = "w-full text-white font-bold py-3 px-4 rounded-lg shadow-md transition-transform transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed";
  const primaryButtonStyle = `${buttonBaseStyle} bg-blue-500 hover:bg-blue-600`;
  const secondaryButtonStyle = `${buttonBaseStyle} bg-gray-600 hover:bg-gray-700`;
  const infoTextStyle = "text-sm text-gray-500";
  // --- End Style Definitions ---

  return (
    <div className="max-w-md mx-auto p-4 space-y-6">
      <h1 className="text-2xl font-bold text-gray-800 text-center">{translation.settings}</h1>

      {/* System Information Card */}
      <div className={cardStyle}>
        <h2 className="text-md font-semibold text-gray-700 mb-3 text-center">System Information</h2>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className={labelStyle}>Status:</span>
            <span className={`${infoTextStyle} font-semibold ${isOnline ? 'text-green-600' : 'text-red-600'}`}>
              {isOnline ? `🟢 ${translation.online}` : `🔴 ${translation.offline}`}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className={labelStyle}>{translation.dataLastSynced}:</span>
            <span className={infoTextStyle}>{lastSynced ? new Date(lastSynced).toLocaleString() : 'N/A'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className={labelStyle}>{translation.version}:</span>
            <span className={infoTextStyle}>{version}</span>
          </div>
        </div>
      </div>

      {/* Language Settings Card */}
      <div className={cardStyle}>
        <label className={labelStyle}>{translation.language}</label>
        <div className="flex rounded-lg shadow-sm">
          <button
            onClick={() => setLanguage('en')}
            className={`flex-1 p-2 rounded-l-lg text-sm font-semibold transition-colors ${language === 'en' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
              }`}
          >
            English
          </button>
          <button
            onClick={() => setLanguage('es')}
            className={`flex-1 p-2 rounded-r-lg text-sm font-semibold transition-colors ${language === 'es' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
              }`}
          >
            Español
          </button>
        </div>
      </div>

      {/* Ticket Lifetime Card */}
      <div className={cardStyle}>
        <label htmlFor="hoursField" className={labelStyle}>
          {translation.hoursToStaySignedIn}
        </label>
        <input
          id="hoursField"
          value={hours ?? ""}
          inputMode='numeric'
          onChange={(e) => changeHours(e.target.value)}
          type="text"
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
        />
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <button onClick={() => window.location.reload()} className={primaryButtonStyle}>
          {translation.refreshPage}
        </button>
        <button onClick={handleRefreshData} disabled={refreshing || !isOnline} className={primaryButtonStyle}>
          {refreshing ? `${translation.refreshing}...` : translation.refreshData}
        </button>
        <button onClick={handleLogout} className={secondaryButtonStyle}>
          {translation.logout}
        </button>
      </div>

    </div>
  );
}

export default Settings;
